/**
 * 团队协作数据层（P-D 团队空间，架构见跨会话记忆「团队协作模块架构定案」）。
 *
 * 并发模型：默认**签出锁·粗粒度**——某人编辑某项目时他人只读（`lock` 字段），带租约；
 * 接真后端前，这里给 mock 种子 + 纯函数（canEdit/lockLabel）+ collabProvider 接缝，
 * 让团队空间页能跑通"共享项目 + 锁状态演示 + 邀请 + 配额"。真同步/权限/租约待接后端。
 */
import { PACKAGES } from './packages';

export type Role = 'owner' | 'admin' | 'editor' | 'viewer' | 'commenter';

export interface Member {
  id: string;
  name: string;
  role: Role;
  online: boolean;
}

/** 编辑锁（谁在编辑）；null/undefined = 无人占用，可编辑。 */
export interface LockInfo {
  userId: string;
  userName: string;
  /** 租约到期时间（ISO）；接真后端由心跳续租 */
  leaseUntil: string;
}

export interface SharedProject {
  id: string;
  name: string;
  cover?: string | undefined;
  memberIds: string[];
  updatedAt: string;
  lock: LockInfo | null;
}

export interface CollabActivity {
  id: string;
  actor: string;
  action: string;
  target: string;
  ts: string;
}

export interface Team {
  id: string;
  name: string;
  plan: 'free' | 'team' | 'enterprise';
  seats: number;
  usedSeats: number;
  storageGB: number;
  usedStorageGB: number;
  creditPool: number;
  members: Member[];
  projects: SharedProject[];
  activity: CollabActivity[];
}

export const ROLE_LABEL: Record<Role, string> = {
  owner: '所有者',
  admin: '管理员',
  editor: '可编辑',
  viewer: '可查看',
  commenter: '仅评论',
};

/* ---------- 团队治理与计费模型（服务器权威；接真后端时落地，现仅数据/校验接缝） ---------- */

/**
 * 套餐限额（席位/云空间/积分池/功能门）——**单一数据源 = packages**（team 档），
 * 避免与定价页/超管后台各写一套造成漂移（字段铁律）。free 为未开通团队的个人态。
 */
export interface PlanLimits {
  seats: number;
  storageGB: number;
  creditPool: number;
  features: string[];
}
const teamStandard = PACKAGES.find((p) => p.kind === 'team' && p.code === 't-standard');
const teamDeluxe = PACKAGES.find((p) => p.kind === 'team' && p.code === 't-deluxe');
export const PLANS: Record<Team['plan'], PlanLimits> = {
  free: { seats: 1, storageGB: 0, creditPool: 0, features: [] },
  team: { seats: teamStandard?.seatsMax ?? 20, storageGB: teamStandard?.storageGb ?? 60, creditPool: 0, features: teamStandard?.features ?? [] },
  enterprise: { seats: teamDeluxe?.seatsMax ?? 20, storageGB: teamDeluxe?.storageGb ?? 120, creditPool: 0, features: teamDeluxe?.features ?? [] },
};

/** 角色→权限动作集（粗粒度 RBAC；真实鉴权在后端）。 */
export type RoleAction = 'invite' | 'removeMember' | 'manageBilling' | 'editProject' | 'comment' | 'view' | 'transferOwner';
export const ROLE_PERMS: Record<Role, RoleAction[]> = {
  owner: ['invite', 'removeMember', 'manageBilling', 'editProject', 'comment', 'view', 'transferOwner'],
  admin: ['invite', 'removeMember', 'editProject', 'comment', 'view'],
  editor: ['editProject', 'comment', 'view'],
  commenter: ['comment', 'view'],
  viewer: ['view'],
};
export function roleCan(role: Role | undefined, action: RoleAction): boolean {
  return role !== undefined && ROLE_PERMS[role].includes(action);
}

/** 席位是否还有空（邀请前置校验）。 */
export function seatAvailable(team: Team): boolean {
  return team.usedSeats < PLANS[team.plan].seats;
}
/** 当前用户能否管理团队（邀请/移除/计费）：owner 或 admin。 */
export function canManageTeam(team: Team, userId: string): boolean {
  const me = team.members.find((m) => m.id === userId);
  return roleCan(me?.role, 'manageBilling') || roleCan(me?.role, 'invite');
}

/** 邀请状态机（邮箱邀请→待接受→已加入/过期/撤销）；成员 id 指向全局用户体系。 */
export type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';
export interface Invitation {
  id: string;
  teamId: string;
  /** 邀请邮箱；接受后解析为全局 user id（与 users 库对接/核查） */
  email: string;
  /** 接受后对应的全局用户 id（未注册则引导注册后回填） */
  userId?: string | undefined;
  role: Role;
  status: InviteStatus;
  invitedBy: string;
  ts: string;
}

/** 当前登录用户（mock 固定，接真后端换鉴权身份）。 */
export const CURRENT_USER_ID = 'u-me';

/** 是否可编辑：无人锁 或 锁在自己手上。纯函数可测。 */
export function canEdit(project: SharedProject, userId: string): boolean {
  return project.lock === null || project.lock.userId === userId;
}

/** 锁状态文案。纯函数可测。 */
export function lockLabel(project: SharedProject, userId: string): string {
  if (project.lock === null) {
    return '可编辑';
  }
  if (project.lock.userId === userId) {
    return '你正在编辑';
  }
  return `${project.lock.userName} 正在编辑 · 只读`;
}

/** mock 种子团队（含"他人锁定的项目"用于演示错峰只读）。 */
export const MOCK_TEAMS: Team[] = [
  {
    id: 't-main',
    name: '星辰短剧组',
    plan: 'team',
    seats: 10,
    usedSeats: 4,
    storageGB: 100,
    usedStorageGB: 37,
    creditPool: 8000,
    members: [
      { id: CURRENT_USER_ID, name: '我', role: 'owner', online: true },
      { id: 'u-lin', name: '林导', role: 'editor', online: true },
      { id: 'u-chen', name: '陈编', role: 'editor', online: false },
      { id: 'u-su', name: '苏美', role: 'commenter', online: true },
    ],
    projects: [
      { id: 'p1', name: '《长夜将尽》第 3 集', memberIds: [CURRENT_USER_ID, 'u-lin'], updatedAt: '2026-10-06T02:00:00.000Z', lock: { userId: 'u-lin', userName: '林导', leaseUntil: '2026-10-06T02:30:00.000Z' } },
      { id: 'p2', name: '《长夜将尽》第 2 集', memberIds: [CURRENT_USER_ID, 'u-chen'], updatedAt: '2026-10-05T09:00:00.000Z', lock: null },
      { id: 'p3', name: '古风样片 · 分镜试验', memberIds: [CURRENT_USER_ID], updatedAt: '2026-10-04T14:00:00.000Z', lock: null },
    ],
    activity: [
      { id: 'a1', actor: '林导', action: '开始编辑', target: '《长夜将尽》第 3 集', ts: '2026-10-06T02:00:00.000Z' },
      { id: 'a2', actor: '苏美', action: '评论了', target: '第 3 集 · 分镜 12', ts: '2026-10-06T01:40:00.000Z' },
      { id: 'a3', actor: '陈编', action: '更新了', target: '文学剧本', ts: '2026-10-05T09:00:00.000Z' },
    ],
  },
];

/** 协作后端接缝（mock；接真时换 remote + /api/v2/team，UI 不变）。 */
export const collabProvider = {
  async listTeams(): Promise<Team[]> {
    return MOCK_TEAMS;
  },
  /** 创建团队（mock）：当前用户为所有者，空项目/动态；配额从 packages 单一源派生。 */
  async createTeam(name: string, plan: Team['plan']): Promise<Team> {
    const limits = PLANS[plan];
    return {
      id: `t-${Math.random().toString(36).slice(2, 7)}`,
      name,
      plan,
      seats: limits.seats,
      usedSeats: 1,
      storageGB: limits.storageGB,
      usedStorageGB: 0,
      creditPool: limits.creditPool,
      members: [{ id: CURRENT_USER_ID, name: '我', role: 'owner', online: true }],
      projects: [],
      activity: [],
    };
  },
  async invite(teamId: string, email: string, role: Role): Promise<{ ok: boolean }> {
    void teamId;
    void email;
    void role;
    return { ok: true };
  },
  /** 申请编辑（对方持锁时）：mock 直接返回已发起。 */
  async requestEdit(projectId: string): Promise<{ ok: boolean }> {
    void projectId;
    return { ok: true };
  },
};
